import axios from 'axios';
import { ActionTree } from 'vuex';
import { DocumentService } from '@/api/documents/services/document.service';
import DocumentViewService from '@/services/document-view.service';
import { IDocumentTracking } from '@/document-tracking/types/document-tracking';
import { translate } from '@/helpers/translation.helper';
import { RootState } from '@/store/store';
import {
  mutationSetState,
  mutationLoadingRecipients,
  mutationSetDocumentData,
  mutationSetLoading,
  mutationSetRecipients,
  mutationSetSender,
  mutationSetTracking,
  mutationSetSelectedRecipientId,
  mutationSetPreviewToken,
  mutationSetCancelScheduleModalVisibility,
  mutationSetTrackClickUpdate,
  mutationSetTrackVideoUpdate,
  mutationSetTrackPageUpdate,
  mutationSetTrackVisitUpdate,
  mutationAddDocumentTag,
  mutationSetDocumentEvents,
  mutationRemoveDocumentTag,
  mutationSetDocumentTags,
} from '@/store/mutations/document-view.mutations';
import { IAddRecipientPayload } from '@/types/AddRecipientPayload';
import { IDocumentViewState, state as defaultState } from '@/store/stores/document-view.store';
import { IRecipient } from '@/types/Recipient';
import { ISender } from '@/types/Sender';
import { IPreviewToken } from '@/dealroom/document/types/preview-token';
import { IPageTracking } from '@/document-tracking/types/page-tracking';
import { IClickTracking } from '@/document-tracking/types/click-tracking';
import { IVideoTracking } from '@/document-tracking/types/video-tracking';
import { IVisitTracking } from '@/document-tracking/types/visit-tracking';
import { IDocument } from '@/types/Document';
import tagService from '@/tags/services/tag.service';
import { ITag } from '@/types/Tag';
import {
  getUpdatedPageTrackings,
  getUpdatedRecipientsLastVisitAndVisitCount,
  getUpdatedRecipientsVisitTime,
} from '@/document-tracking/helpers/tracking.helper';
import { IDocumentEventItem } from '@/document-view/types/document-events-item';

export const actionLoadDocumentData = 'loadDocument';
export const actionLoadDocumentDataSuccess = 'loadDocumentDataSuccess';
export const actionLoadDocumentDataFailed = 'loadDocumentDataFailed';

export const actionAddRecipient = 'addRecipient';
export const actionLoadRecipients = 'loadRecipients';
export const actionResetState = 'resetState';

export const actionLoadTracking = 'loadTracking';
export const actionLoadTrackingSuccess = 'loadTrackingSuccess';
export const actionLoadTrackingFailed = 'loadTrackingFailed';

export const actionSelectRecipient = 'selectRecipient';
export const actionGetPreviewToken = 'getPreviewToken';
export const actionGetPreviewTokenSuccess = 'getPreviewTokenSuccess';
export const actionGetPreviewTokenFailed = 'getPreviewTokenFailed';

export const actionToggleCancelScheduleModal = 'toggleCancelScheduleModal';

export const actionAddTrackClickUpdate = 'addTrackClickUpdate';
export const actionAddTrackVideoUpdate = 'addTrackVideoUpdate';
export const actionAddTrackPageUpdate = 'addTrackPageUpdate';
export const actionAddTrackVisitUpdate = 'addTrackVisitUpdate';

export const actionAddDocumentTag = 'addDocumentTag';
export const actionAddDocumentTagFailed = 'addDocumentTagFailed';

export const actionRemoveDocumentTag = 'removeDocumentTag';
export const actionRemoveDocumentTagSuccess = 'removeDocumentTagSuccess';
export const actionRemoveDocumentTagFailed = 'removeDocumentTagFailed';
export const actionLoadDocumentEvents = 'actionLoadDocumentEvents';
export const actionLoadDocumentEventsSuccess = 'actionLoadDocumentEventsSuccess';

function recipientThumbUrl(recipient) {
  if (recipient.thumb_url === '') {
    return '/assets/img/default-avatar.png';
  }
  return recipient.thumb_url;
}

export const actions: ActionTree<IDocumentViewState, RootState> = {
  async [actionLoadDocumentData]({ commit, dispatch }, documentId: string) {
    commit(mutationSetLoading, true);
    try {
      const document: IDocument = await DocumentService.loadDocument(documentId);
      //should be changed/removed later on
      const senderData: ISender = {
        name: document.user.fullName,
        email: document.user.email,
        senderId: document.user.id,
        thumbUrl: document.user.thumbUrl,
      };
      dispatch(actionLoadDocumentDataSuccess, { document, senderData });
    } catch {
      dispatch(actionLoadDocumentDataFailed);
    }
  },
  [actionLoadDocumentDataSuccess](
    { commit },
    { document, senderData }: { document: IDocument; senderData: ISender }
  ) {
    commit(mutationSetSender, senderData);
    commit(mutationSetDocumentData, document);
    commit(mutationSetLoading, false);
  },
  async [actionLoadDocumentDataFailed]({ commit }) {
    commit(mutationSetLoading, false);
  },
  async [actionAddRecipient](_, addRecipientPayload: IAddRecipientPayload) {
    const body = new FormData();
    const sendManually = addRecipientPayload.sendManually
      ? addRecipientPayload.sendManually.toString()
      : '';

    body.append('action', 'addrecipient');
    body.append('document-id', addRecipientPayload.documentId);
    body.append('document-recipient_email', addRecipientPayload.email);
    body.append('document-recipient_first_name', addRecipientPayload.firstName);
    body.append('document-recipient_last_name', addRecipientPayload.lastName);
    body.append('document-recipient_role', addRecipientPayload.role);
    body.append('send-manually', sendManually);

    const {
      data: { status, recipient_id },
    } = await axios.post(`/save/board`, body);
    if (status !== 1) {
      return false;
    }
    return recipient_id;
  },
  async [actionLoadRecipients]({ commit }, documentId: string) {
    commit(mutationLoadingRecipients, true);
    const {
      data: { result = [] },
    } = await axios.get(`/get/recipient/${documentId}/overview`);
    const recipients: IRecipient[] = result.map(recipient => ({
      companyName: recipient.company_name,
      fullName: recipient.fullname || `${recipient.first_name} ${recipient.last_name}`.trim(),
      firstName: recipient.first_name,
      email: recipient.email,
      lastName: recipient.last_name,
      id: recipient.recipient_id,
      recipientId: recipient.recipient_id,
      mobile: recipient.mobile,
      role: recipient.role,
      roleName: recipient.role_name,
      thumbUrl: recipientThumbUrl(recipient),
      verifyEid: recipient.verify_eid,
      verifyLinkedin: recipient.verify_linkedin,
      verifyQna: recipient.verify_qna,
      verifySms: recipient.verify_sms,
      gender: recipient.gender,
      orderNum: recipient.order_num,
      status: recipient.status,
      recipientLastVisitTime: recipient.recipient_last_visit_time,
      recipientVisitCount: recipient.recipient_visit_count,
      recipientVisitTime: recipient.recipient_visit_time,
      forwarded: recipient.forwarded,
    }));
    commit(mutationSetRecipients, recipients);
    commit(mutationLoadingRecipients, false);
  },
  async [actionLoadTracking](
    { dispatch },
    { documentId }: { documentId: string; recipientId?: string }
  ) {
    try {
      const tracking: IDocumentTracking = await DocumentService.loadTracking(documentId);
      dispatch(actionLoadTrackingSuccess, tracking);
    } catch (error) {
      dispatch(actionLoadTrackingFailed, error);
    }
  },
  [actionLoadTrackingSuccess]({ commit }, tracking: IDocumentTracking) {
    commit(mutationSetTracking, tracking);
  },
  [actionLoadTrackingFailed]({ dispatch }) {
    dispatch('alert/showDangerAlert', translate('failed-to-get-page-statistics'), {
      root: true,
    });
  },
  [actionResetState]({ commit }) {
    commit(mutationSetState, defaultState);
  },
  [actionSelectRecipient]({ commit }, recipientId: string) {
    commit(mutationSetSelectedRecipientId, recipientId);
  },
  async [actionGetPreviewToken]({ dispatch }, documentId: string) {
    try {
      const previewToken = await DocumentService.getPreviewToken(documentId);
      dispatch(actionGetPreviewTokenSuccess, previewToken);
    } catch {
      dispatch(actionGetPreviewTokenFailed);
    }
  },
  [actionGetPreviewTokenSuccess]({ commit }, previewToken: IPreviewToken) {
    commit(mutationSetPreviewToken, previewToken);
  },
  [actionGetPreviewTokenFailed]({ dispatch }) {
    dispatch('alert/showDangerAlert', translate('failed-to-preview-document'), {
      root: true,
    });
  },
  [actionToggleCancelScheduleModal]({ commit }, show: boolean) {
    commit(mutationSetCancelScheduleModalVisibility, show);
  },
  [actionAddTrackClickUpdate]({ commit }, trackClickUpdate: IClickTracking) {
    commit(mutationSetTrackClickUpdate, trackClickUpdate);
  },
  [actionAddTrackVideoUpdate]({ commit }, trackVideoUpdate: IVideoTracking) {
    commit(mutationSetTrackVideoUpdate, trackVideoUpdate);
  },
  [actionAddTrackPageUpdate]({ commit, state }, trackPageUpdate: IPageTracking) {
    const updatedPageTrackings: IPageTracking[] = getUpdatedPageTrackings(
      state.tracking.pageTrackings,
      trackPageUpdate
    );
    commit(mutationSetTrackPageUpdate, updatedPageTrackings);
    const updatedRecipients: IRecipient[] = getUpdatedRecipientsVisitTime(
      state.tracking.pageTrackings,
      state.recipients
    );
    commit(mutationSetRecipients, updatedRecipients);
  },
  [actionAddTrackVisitUpdate]({ commit, state }, trackVisitUpdate: IVisitTracking) {
    commit(mutationSetTrackVisitUpdate, trackVisitUpdate);
    const updatedRecipients: IRecipient[] = getUpdatedRecipientsLastVisitAndVisitCount(
      state.tracking.visitTrackings,
      state.recipients
    );
    commit(mutationSetRecipients, updatedRecipients);
  },
  async [actionLoadDocumentEvents]({ dispatch, commit }, documentId: string) {
    try {
      const events: IDocumentEventItem[] = await DocumentViewService.loadDocumentEvents(documentId);
      commit(mutationSetDocumentEvents, events);
    } catch (error) {
      dispatch('alert/showDangerAlert', translate('could-not-load-document-events'), {
        root: true,
      });
    }
  },
  async [actionAddDocumentTag]({ dispatch, commit, state }, tag: ITag) {
    commit(mutationAddDocumentTag, tag);
    try {
      await tagService.addDocumentTags([state.document.id], [tag.id]);
    } catch (error) {
      dispatch(actionAddDocumentTagFailed, { tag });
    }
  },
  [actionAddDocumentTagFailed]({ dispatch, commit, state }, tag: ITag) {
    const oldTags: ITag[] = state.document.tags.filter((oldTag: ITag) => oldTag.id !== tag.id);
    commit(mutationSetDocumentTags, oldTags);
    dispatch(
      'alert/showDangerAlert',
      translate('Failed to add tag "{title}"', { title: tag.title }),
      {
        root: true,
      }
    );
  },
  async [actionRemoveDocumentTag]({ dispatch, state }, tag: ITag) {
    try {
      await tagService.removeDocumentTags([state.document.id], [tag.id]);
      dispatch(actionRemoveDocumentTagSuccess, tag.id);
    } catch {
      dispatch(actionRemoveDocumentTagFailed);
    }
  },
  [actionRemoveDocumentTagSuccess]({ commit }, tagId: string) {
    commit(mutationRemoveDocumentTag, tagId);
  },
  async [actionRemoveDocumentTagFailed]({ dispatch }) {
    dispatch('alert/showDangerAlert', translate('could-not-remove-tag-from-document'), {
      root: true,
    });
  },
};
